import type { ComponentProps, TouchEvent } from 'react';
import React, { Suspense, useId } from 'react';
import styled from '@emotion/styled';
import type { BorderBottomProps, BorderColorProps, OverflowProps, SpaceProps } from 'styled-system';
import { borderBottom, borderColor, overflow, space } from 'styled-system';

import ContainerWithBackground from 'common/components/ContainerWithBackground';
import { GrowthbookFlag } from 'common/constants/feature-flags';
import { RecoSliderPosition } from 'common/enums/RecommendationsSlider';
import { useFeatureFlagIsOn } from 'common/hooks/feature-flags';
import { useIsObserved } from 'common/hooks/intersection-observer';
import theme from 'common/theme/theme';
import { useSliderConfig } from './hooks/recommendations/config';
import RecommendationsSliderLazy from './recommendations-slider.lazy';
import RecommendationsSliderStatic from './RecommendationsSliderStatic';
import type {
  RecommendationsSliderBaseProps,
  RecommendationsSliderConfig,
  RecommendationsSliderLazyProps,
} from './types';

type RecommendationsSliderProps = RecommendationsSliderBaseProps | RecommendationsSliderLazyProps;

const UnstyledRecommendationsContainer = styled.div<
  OverflowProps & BorderBottomProps & BorderColorProps & SpaceProps
>`
  ${overflow};
  ${borderBottom};
  ${borderColor};
  ${space};
  margin-left: -${theme.space.sp_8}px;
`;

const RecommendationsContainer = (
  props: ComponentProps<typeof UnstyledRecommendationsContainer>
) => <UnstyledRecommendationsContainer overflow="hidden" p="sp_8" pr="0" {...props} />;

const UnstyledStyledContainerWithBackground = styled(ContainerWithBackground)<
  SpaceProps & OverflowProps
>`
  ${space};
  ${overflow};
`;

const StyledContainerWithBackground = (
  props: ComponentProps<typeof UnstyledStyledContainerWithBackground>
) => <UnstyledStyledContainerWithBackground overflow="hidden" {...props} />;

const isLazy = (props: RecommendationsSliderProps): props is RecommendationsSliderLazyProps =>
  'sku' in props || 'variables' in props;

const getSlider = (props: RecommendationsSliderProps, config: RecommendationsSliderConfig) => {
  if (isLazy(props)) {
    return config.isObserved ? (
      <Suspense fallback={null}>
        <RecommendationsSliderLazy {...props} config={config} isLazy />
      </Suspense>
    ) : null;
  }

  return <RecommendationsSliderStatic {...props} config={config} />;
};

const getContainer = (withBackground?: boolean) =>
  withBackground ? StyledContainerWithBackground : RecommendationsContainer;

const getBorderProps = (withSeparator?: boolean) =>
  withSeparator
    ? {
        borderBottom: '1px solid',
        borderColor: 'sys.neutral.border.weak',
      }
    : null;

const RecommendationsSlider = ({
  position = RecoSliderPosition.Default,
  ...props
}: RecommendationsSliderProps) => {
  const titleId = useId();
  const { className, withBackground, withSeparator } = props;
  const [ref, isObserved] = useIsObserved();
  const isSmallVariant = useFeatureFlagIsOn(GrowthbookFlag.RecommendationsSliderSmaller);
  const config = useSliderConfig({
    position,
    withBackground,
    isObserved,
    isSmallVariant,
  });
  const Container = getContainer(withBackground);
  const handleTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <Container
      as="aside"
      data-corgi-id="recommendations-slider"
      ref={ref}
      className={className}
      onTouchEnd={handleTouchEnd}
      data-testid="recommendations-slider"
      {...(isObserved ? { 'aria-labelledby': titleId } : {})}
      {...getBorderProps(withSeparator)}
    >
      {getSlider({ ...props, titleId }, config)}
    </Container>
  );
};

export default RecommendationsSlider;
